import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { montantEnLettres, type ReceiptData } from "./receipt";
import { formatMoney } from "./format";
import type { SchoolInfo } from "@/types";
import { currencyLabel } from "./currency";

const fg = (n: number) => formatMoney(n);

function frDate(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Génère et télécharge un reçu de paiement au format PDF (A4 portrait).
 * Les couleurs sont fixes (RGB) : un PDF ne lit pas les variables de thème CSS.
 */
export function exportReceiptPdf(data: ReceiptData, school: SchoolInfo): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  const contentWidth = pageWidth - margin * 2;

  // Bandeau école
  doc.setFillColor(29, 95, 208);
  doc.rect(margin, 34, contentWidth, 46, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(school.name.toUpperCase(), margin + 12, 56);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text(`${school.address} — ${school.city}`, margin + 12, 69);

  // Bloc numéro / date
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(`Reçu N° ${data.receiptNo}`, pageWidth - margin - 12, 56, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Date : ${frDate(data.date)}`, pageWidth - margin - 12, 69, { align: "right" });

  // Titre
  doc.setDrawColor(206, 206, 200);
  doc.setFillColor(240, 246, 242);
  doc.rect(margin, 90, contentWidth, 24, "FD");
  doc.setTextColor(14, 42, 92);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(data.title.toUpperCase(), pageWidth / 2, 106, { align: "center" });

  // Avertissement (reçu provisoire)
  if (data.notice) {
    doc.setDrawColor(214, 158, 20);
    doc.setFillColor(254, 247, 224);
    doc.rect(margin, 120, contentWidth, 34, "FD");
    doc.setTextColor(120, 80, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(doc.splitTextToSize(data.notice, contentWidth - 20) as string[], pageWidth / 2, 134, { align: "center" });
  }

  // Informations élève
  doc.setTextColor(55, 55, 52);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const infoY = data.notice ? 172 : 132;
  doc.setFont("helvetica", "bold");
  doc.text("Élève :", margin, infoY);
  doc.setFont("helvetica", "normal");
  doc.text(`${data.student.name}  (${data.student.matricule})`, margin + 46, infoY);
  doc.setFont("helvetica", "bold");
  doc.text("Classe :", margin, infoY + 16);
  doc.setFont("helvetica", "normal");
  doc.text(`${data.student.className} — ${data.student.cycleLabel}`, margin + 46, infoY + 16);
  doc.setFont("helvetica", "bold");
  doc.text("Année :", pageWidth / 2 + 20, infoY + 16);
  doc.setFont("helvetica", "normal");
  doc.text(data.student.schoolYear, pageWidth / 2 + 66, infoY + 16);

  // Montant reçu
  const amountY = infoY + 36;
  doc.setDrawColor(180, 210, 195);
  doc.setFillColor(240, 246, 242);
  doc.rect(margin, amountY, contentWidth, 66, "FD");
  doc.setTextColor(14, 42, 92);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("MONTANT REÇU", margin + 12, amountY + 18);
  doc.setFontSize(18);
  doc.text(fg(data.amountReceived), margin + 12, amountY + 40);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(80, 80, 76);
  doc.text(montantEnLettres(data.amountReceived), margin + 12, amountY + 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(55, 55, 52);
  doc.text(`Mode : ${data.method}`, pageWidth - margin - 12, amountY + 20, { align: "right" });

  // Tableau des frais
  autoTable(doc, {
    startY: amountY + 72,
    head: [["Désignation", "Montant dû", "Payé", "Reste"]],
    body: data.lines.map((l) => [
      l.label,
      formatMoney(l.due),
      formatMoney(l.paid),
      formatMoney(l.remaining),
    ]),
    foot: [["Total", formatMoney(data.totalDue), formatMoney(data.totalPaid), formatMoney(data.remaining)]],
    theme: "grid",
    styles: { font: "helvetica", fontSize: 9, cellPadding: 5, textColor: [55, 55, 52], lineColor: [206, 206, 200], lineWidth: 0.5 },
    headStyles: { fillColor: [234, 238, 245], textColor: [55, 60, 55], fontStyle: "bold" },
    footStyles: { fillColor: [226, 236, 252], textColor: [14, 42, 92], fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 92, halign: "right" },
      2: { cellWidth: 92, halign: "right" },
      3: { cellWidth: 92, halign: "right" },
    },
  });

  let cursorY = ((doc as any).lastAutoTable?.finalY ?? amountY + 140) + 16;

  // Détail des versements
  if (data.versements.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
    doc.text("Détail des versements", margin, cursorY);
    cursorY += 6;
    autoTable(doc, {
      startY: cursorY,
      head: [["N° reçu", "Date", "Désignation", "Mode", "Montant"]],
      body: data.versements.map((v) => [
        v.receiptNo,
        frDate(v.date),
        v.label,
        v.method,
        fg(v.amount),
      ]),
      theme: "grid",
      styles: { font: "helvetica", fontSize: 8.5, cellPadding: 4, textColor: [55, 55, 52], lineColor: [206, 206, 200], lineWidth: 0.4 },
      headStyles: { fillColor: [234, 238, 245], textColor: [55, 60, 55], fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 78 },
        1: { cellWidth: 66 },
        2: { cellWidth: "auto" },
        3: { cellWidth: 92 },
        4: { cellWidth: 82, halign: "right" },
      },
    });
    cursorY = ((doc as any).lastAutoTable?.finalY ?? cursorY) + 20;
  }

  if (data.note) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 90, 86);
    doc.text(`Note : ${data.note}`, margin, cursorY);
    cursorY += 16;
  }

  // Signatures
  const sigY = Math.max(cursorY + 10, 720);
  doc.setDrawColor(150, 150, 145);
  doc.line(margin, sigY, margin + 160, sigY);
  doc.line(pageWidth - margin - 160, sigY, pageWidth - margin, sigY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(55, 55, 52);
  doc.text("Le comptable", margin, sigY + 14);
  doc.text("Le parent / tuteur", pageWidth - margin - 160, sigY + 14);

  doc.setFontSize(8);
  doc.setTextColor(120, 120, 116);
  doc.text(
    `${data.notice ? "Reçu provisoire établi" : "Reçu établi"} en deux exemplaires : un pour l'établissement, un pour le parent. Devise : ${currencyLabel()}.`,
    pageWidth / 2,
    812,
    { align: "center" },
  );

  doc.save(`${data.receiptNo || "recu"}.pdf`);
}